import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useAuth, canDelete, can, type Permission } from '../lib/auth';
import { useRememberListUrl, listReturnUrl } from '../lib/listReturn';

export type FieldType = 'text' | 'textarea' | 'number' | 'bool' | 'date' | 'email' | 'lookup';

export interface FieldDef {
  /** API field name (matches SimpleMaster column .api) */
  name: string;
  label: string;
  type?: FieldType;
  /** column shown in the list grid */
  list?: boolean;
  /** required for create/update */
  required?: boolean;
  /** placeholder */
  placeholder?: string;
  /** For type: 'lookup' — endpoint that returns [{id, name}] options. */
  lookupEndpoint?: string;
  displayFormatter?: (v: unknown) => string;
  valueParser?: (v: string) => unknown;
  /** Card heading the field sits under; consecutive fields with the same one share it. */
  section?: string;
  /** Spans both form columns. */
  wide?: boolean;
  /** Longest a text may be — the column's size. */
  max?: number;
  /** Offered as you type; anything else may still be typed. */
  suggestions?: readonly string[];
}

interface LookupOption { id: string; name: string }

export interface SimpleMasterPageConfig {
  /** REST base, e.g. "/api/vendors" */
  apiBase: string;
  /** Route prefix, e.g. "/vendors" */
  routeBase: string;
  /** Singular noun shown in title — "Vendor" */
  singular: string;
  /** Plural for index page — "Vendors" */
  plural: string;
  fields: FieldDef[];
  /** Page size for list */
  pageSize?: number;
  /** What adding / editing needs; EDIT_MASTERS unless set (matches the API). */
  editPerm?: Permission;
  /** Has a printable form at `${routeBase}/:id/print` (and a blank one at /blank/print). */
  printable?: boolean;
}

interface Row {
  id: string;
  [k: string]: unknown;
}

interface ListResp { rows: Row[]; total: number; page: number; pageSize: number }

export function SimpleMasterList({ cfg }: { cfg: SimpleMasterPageConfig }) {
  useRememberListUrl();
  const PAGE_SIZE = cfg.pageSize ?? 50;
  const [params, setParams] = useSearchParams();
  const page = Math.max(1, Number(params.get('page') ?? '1'));
  const q = params.get('q') ?? '';
  const [search, setSearch] = useState(q);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const qc = useQueryClient();

  const list = useQuery({
    queryKey: [cfg.apiBase, { page, q }],
    queryFn: () =>
      api<ListResp>(
        `${cfg.apiBase}?page=${page}&pageSize=${PAGE_SIZE}` + (q ? `&q=${encodeURIComponent(q)}` : ''),
      ),
  });

  const { user } = useAuth();
  const del = useMutation({
    mutationFn: (ids: string[]) =>
      api(`${cfg.apiBase}/delete`, { method: 'POST', body: JSON.stringify({ ids }) }),
    onSuccess: () => {
      setSelected(new Set());
      qc.invalidateQueries({ queryKey: [cfg.apiBase] });
    },
  });

  function applySearch(e: FormEvent) {
    e.preventDefault();
    const p = new URLSearchParams(params);
    if (search) p.set('q', search); else p.delete('q');
    p.set('page', '1');
    setParams(p);
  }
  function goPage(next: number) {
    const p = new URLSearchParams(params);
    p.set('page', String(next));
    setParams(p);
  }
  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }
  function toggleAll(rows: Row[]) {
    setSelected((prev) => prev.size === rows.length ? new Set() : new Set(rows.map((r) => r.id)));
  }

  const listFields = cfg.fields.filter((f) => f.list);
  const rows = list.data?.rows ?? [];
  const total = list.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));



  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-brand">{cfg.plural}</h1>
          <p className="text-xs text-slate-500">{total.toLocaleString()} active</p>
        </div>
        <div className="flex gap-2">
          {cfg.printable && (
            <Link to={`${cfg.routeBase}/blank/print`} className="btn-ghost" title="An empty form to print and fill in by hand">
              Blank Form
            </Link>
          )}
          {can(user, cfg.editPerm ?? 'EDIT_MASTERS') && (
            <Link to={`${cfg.routeBase}/new`} className="btn-primary">+ New {cfg.singular}</Link>
          )}
        </div>
      </div>

      <form className="flex flex-wrap items-end gap-2" onSubmit={applySearch}>
        <div className="flex-1 min-w-[240px]">
          <label className="label">Search</label>
          <input className="input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Type to search…" />
        </div>
        <button className="btn-primary" type="submit">Search</button>
        {q && (
          <button type="button" className="btn-ghost" onClick={() => { setSearch(''); setParams({}); }}>
            Reset
          </button>
        )}
        {canDelete(user) && selected.size > 0 && (
          <button
            type="button" className="btn-danger" disabled={del.isPending}
            onClick={() => { if (confirm(`Soft-delete ${selected.size}?`)) del.mutate(Array.from(selected)); }}
          >
            Delete {selected.size}
          </button>
        )}
      </form>

      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-100 text-left text-xs uppercase text-slate-600">
              <tr>
                <th className="px-3 py-2 w-8">
                  <input type="checkbox" checked={rows.length > 0 && selected.size === rows.length} onChange={() => toggleAll(rows)} />
                </th>
                <th className="px-3 py-2">ID</th>
                {listFields.map((f) => <th key={f.name} className="px-3 py-2">{f.label}</th>)}
                <th className="px-3 py-2 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {list.isLoading && (
                <tr><td colSpan={listFields.length + 3} className="px-3 py-8 text-center text-slate-500">Loading…</td></tr>
              )}
              {list.isError && (
                <tr><td colSpan={listFields.length + 3} className="px-3 py-8 text-center text-red-600">
                  Failed to load: {(list.error as Error).message}
                </td></tr>
              )}
              {!list.isLoading && rows.length === 0 && (
                <tr><td colSpan={listFields.length + 3} className="px-3 py-8 text-center text-slate-500">No records found.</td></tr>
              )}
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-3 py-2">
                    <input type="checkbox" checked={selected.has(r.id)} onChange={() => toggle(r.id)} />
                  </td>
                  <td className="px-3 py-2 font-mono text-xs">{r.id}</td>
                  {listFields.map((f) => (
                    <td key={f.name} className="px-3 py-2 text-slate-700">
                      {f.type === 'lookup' && f.lookupEndpoint
                        ? <LookupCell value={r[f.name]} lookupEndpoint={f.lookupEndpoint} fallback={formatCell(r[f.name], f.type, f.displayFormatter)} />
                        : formatCell(r[f.name], f.type, f.displayFormatter)}
                    </td>
                  ))}
                  <td className="px-3 py-2 text-right space-x-2 whitespace-nowrap">
                    {cfg.printable && (
                      <Link to={`${cfg.routeBase}/${r.id}/print`} className="text-slate-600 hover:underline text-sm">Print</Link>
                    )}
                    <Link to={`${cfg.routeBase}/${r.id}/edit`} className="text-brand hover:underline text-sm">Edit</Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex items-center justify-between text-sm">
        <div className="text-slate-500">Page {page} of {totalPages}</div>
        <div className="flex gap-2">
          <button className="btn-ghost" disabled={page <= 1} onClick={() => goPage(page - 1)}>‹ Prev</button>
          <button className="btn-ghost" disabled={page >= totalPages} onClick={() => goPage(page + 1)}>Next ›</button>
        </div>
      </div>
    </div>
  );
}

function formatCell(v: unknown, type?: FieldType, displayFormatter?: (v: unknown) => string): string {
  if (displayFormatter) return displayFormatter(v);
  if (v == null || v === '') return '';
  if (type === 'bool') return v ? 'Yes' : 'No';
  if (type === 'date' && typeof v === 'string') {
    const d = new Date(v);
    if (!isNaN(d.getTime())) return d.toISOString().slice(0, 10);
  }
  return String(v);
}

export function SimpleMasterForm({ cfg }: { cfg: SimpleMasterPageConfig }) {
  // Master records are readable office-wide, writable by Super Admin / VPPL
  // (and vendors by Purchase too — cfg.editPerm).
  const { user } = useAuth();
  const canEditMasters = can(user, cfg.editPerm ?? 'EDIT_MASTERS');
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const qc = useQueryClient();

  const initial = useMemo(() => {
    const v: Record<string, unknown> = {};
    for (const f of cfg.fields) {
      v[f.name] = f.type === 'bool' ? false : '';
    }
    return v;
  }, [cfg.fields]);

  const [form, setForm] = useState<Record<string, unknown>>(initial);
  const [error, setError] = useState<string | null>(null);

  const existing = useQuery({
    enabled: isEdit,
    queryKey: [cfg.apiBase, id],
    queryFn: () => api<Record<string, unknown>>(`${cfg.apiBase}/${id}`),
  });

  useEffect(() => {
    if (existing.data) {
      const next: Record<string, unknown> = { ...initial };
      for (const f of cfg.fields) {
        let v = existing.data[f.name];
        if (f.displayFormatter && v != null) {
          v = f.displayFormatter(v);
        } else if (v != null) {
          v = f.type === 'date' && typeof v === 'string'
            ? v.slice(0, 10)
            : f.type === 'bool' ? Boolean(v) : v;
        }
        next[f.name] = v ?? '';
      }
      setForm(next);
    }
  }, [existing.data, cfg.fields, initial]);

  const save = useMutation({
    mutationFn: () => {
      const payload: Record<string, unknown> = { ...form };
      for (const f of cfg.fields) {
        if (f.valueParser && typeof payload[f.name] === 'string') {
          payload[f.name] = f.valueParser(payload[f.name] as string);
        }
      }
      return api<{ id: string }>(isEdit ? `${cfg.apiBase}/${id}` : cfg.apiBase, {
        method: isEdit ? 'PUT' : 'POST',
        body: JSON.stringify(payload),
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [cfg.apiBase] });
      if (isEdit) qc.invalidateQueries({ queryKey: [cfg.apiBase, id] });
      // Also invalidate any lookup-derived caches keyed off this master, so
      // dropdowns elsewhere in the app immediately see the new row.
      qc.invalidateQueries({ queryKey: ['lookup-options'] });
      qc.invalidateQueries({ queryKey: ['sheets-for-paper'] });
      qc.invalidateQueries({ queryKey: ['lookup'] });
      navigate(listReturnUrl(cfg.routeBase));
    },
    onError: (e) => setError((e as Error).message),
  });

  function update(k: string, v: unknown) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    for (const f of cfg.fields) {
      if (f.required && !String(form[f.name] ?? '').trim()) {
        setError(`${f.label} is required.`);
        return;
      }
    }
    save.mutate();
  }

  // Consecutive fields with the same section share a heading; a field
  // without one goes under "<Singular> Details".
  const groups = useMemo(() => {
    const out: Array<{ title: string; fields: FieldDef[] }> = [];
    for (const f of cfg.fields) {
      const title = f.section ?? `${cfg.singular} Details`;
      const last = out[out.length - 1];
      if (last?.title === title) last.fields.push(f);
      else out.push({ title, fields: [f] });
    }
    return out;
  }, [cfg.fields, cfg.singular]);

  if (isEdit && existing.isLoading) return <div className="p-6 text-slate-500">Loading…</div>;

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-semibold text-brand">
          {isEdit ? `Edit ${cfg.singular} — ${id}` : `New ${cfg.singular}`}
        </h1>
        <div className="flex items-center gap-4">
          {cfg.printable && isEdit && (
            <Link to={`${cfg.routeBase}/${id}/print`} className="text-sm text-slate-600 hover:underline">Print Form</Link>
          )}
          <Link to={listReturnUrl(cfg.routeBase)} className="text-sm text-slate-600 hover:underline">← Back</Link>
        </div>
      </div>

      {!canEditMasters && (
        <div className="mb-4 rounded border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">
          View only — {cfg.plural.toLowerCase()} are maintained by Super Admin and VPPL.
        </div>
      )}

      <form className="card" onSubmit={onSubmit}>
        {/* One disabled fieldset covers every generated field at once, so a new
            column added to a master config cannot arrive editable. */}
        <fieldset disabled={!canEditMasters} className="disabled:opacity-100">
        {groups.map((g, i) => (
          <div key={`${i}-${g.title}`}>
            <div className={i ? 'card-header mt-2' : 'card-header'}>{g.title}</div>
            <div className="card-body grid gap-4 md:grid-cols-2">
              {g.fields.map((f) => (
                <FieldInput key={f.name} f={f} value={form[f.name]} onChange={(v) => update(f.name, v)} />
              ))}
            </div>
          </div>
        ))}
        </fieldset>
        {error && (
          <div className="px-4 pb-3">
            <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
          </div>
        )}
        <div className="border-t border-slate-200 px-4 py-3 flex justify-end gap-2">
          <Link to={listReturnUrl(cfg.routeBase)} className="btn-ghost">
            {canEditMasters ? 'Cancel' : 'Back'}
          </Link>
          {canEditMasters && (
            <button type="submit" className="btn-primary" disabled={save.isPending}>
              {save.isPending ? 'Saving…' : isEdit ? 'Update' : 'Create'}
            </button>
          )}
        </div>
      </form>
    </div>
  );
}

function FieldInput({ f, value, onChange }: { f: FieldDef; value: unknown; onChange: (v: unknown) => void }) {
  if (f.type === 'bool') {
    return (
      <label className="flex items-center gap-2 mt-6">
        <input type="checkbox" checked={Boolean(value)} onChange={(e) => onChange(e.target.checked)} />
        <span className="text-sm">{f.label}</span>
      </label>
    );
  }
  if (f.type === 'lookup' && f.lookupEndpoint) {
    return <LookupSelect f={f} value={value} onChange={onChange} />;
  }
  const listId = f.suggestions ? `suggest-${f.name}` : undefined;
  return (
    <div className={f.wide ? 'md:col-span-2' : undefined}>
      <label className="label">{f.label}{f.required && ' *'}</label>
      {f.type === 'textarea' ? (
        <textarea
          className="input min-h-[80px]" value={String(value ?? '')}
          onChange={(e) => onChange(e.target.value)} placeholder={f.placeholder} maxLength={f.max}
        />
      ) : (
        <input
          className="input"
          type={f.type === 'number' ? 'number' : f.type === 'date' ? 'date' : f.type === 'email' ? 'email' : 'text'}
          value={String(value ?? '')}
          onChange={(e) => onChange(e.target.value)}
          placeholder={f.placeholder}
          maxLength={f.max}
          list={listId}
        />
      )}
      {listId && (
        <datalist id={listId}>
          {f.suggestions!.map((s) => <option key={s} value={s} />)}
        </datalist>
      )}
    </div>
  );
}

function LookupSelect({ f, value, onChange }:
  { f: FieldDef; value: unknown; onChange: (v: unknown) => void }) {
  const q = useQuery({
    queryKey: ['lookup-options', f.lookupEndpoint],
    queryFn: () => api<LookupOption[]>(f.lookupEndpoint!),
    staleTime: 5 * 60_000,
  });
  const options = q.data ?? [];
  const current = String(value ?? '');
  const hasCurrent = current === '' || options.some((o) => o.id === current);
  return (
    <div>
      <label className="label">{f.label}{f.required && ' *'}</label>
      <select
        className="input"
        value={current}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">{q.isLoading ? 'Loading…' : '—'}</option>
        {options.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
        {!hasCurrent && <option value={current}>{current} (legacy)</option>}
      </select>
    </div>
  );
}

function LookupCell({ value, lookupEndpoint, fallback }: { value: unknown; lookupEndpoint: string; fallback: string }) {
  const q = useQuery({
    queryKey: ['lookup-options', lookupEndpoint],
    queryFn: () => api<LookupOption[]>(lookupEndpoint),
    staleTime: 5 * 60_000,
  });
  
  // Safe array query mapping
  const m = useMemo(() => {
    const map = new Map<string, string>();
    const data = q.data;
    if (Array.isArray(data)) {
      for (const o of data) {
        map.set(o.id, o.name);
      }
    }
    return map;
  }, [q.data]);

  return <>{m.get(String(value ?? '')) ?? fallback}</>;
}
